import {ISeoCache, ISeoPage} from "./types/types";
import {EnumScreenNames} from "../../enums/EnumScreenNames";
import {Localization} from "./classes/Localization.class";
import {StaticContentsAdapter} from "./classes/adapters/StaticContentsAdapter";
import {SingleNodeAdapter} from "./classes/adapters/SingleNodeAdapter";
import {SpecsSeoAdapter} from "./classes/adapters/SpecsSeoAdapter";
import {IFindPageQueryString, IMetaData, IPostData, IPostsApiResponse, IPostsByTagsApiResponse,} from "models/post";
import {SeoItemAdapter} from "./classes/adapters/SeoItemAdapter";
import EssentialData from "../../private/classes/EssentialData.class";
import {ILiteRouterApiResponse} from "models/ILiteRouterApiResponse";
import {ICountryAsset} from "models/Assets/ICountryAsset";
import LandingApiResponse from "models/LandingApiModels";
import {ICategoryAsset, ICategoryLiteAsset,} from "models/Assets/ICategoryAsset";
import {ICityAsset} from "models/Assets/ICityAsset";
import {ISelectedAutoServiceCenterObjects} from "dataLayer/filters/autoServicesCenters";
import {ISelectedTabletMobileSpecsObjects} from "dataLayer/filters/tabletMobileSpecs";
import {ISelectedCarPricesSpecsObjects} from "dataLayer/filters/carPricesSpecs";
import {IBankDetails} from "models/loanCalculator";
import {ISelectedRealEstatePricesObjects} from "dataLayer/filters/realEstatePrices";
import {IFaqCategory} from "models/helpCenter";
import {IServiceCenter} from "models/autoServices";
import {labelHelper, urlHelper} from "helpers/themeHelpers";
import {EnumSeoItemLevel} from "./models/SeoItemModel";
import {ICarPricesSpecsCarSpecs} from "models/SpecsModals/carPricesSpecs";
import {IOtherModels} from "models/mobileTabletSpecs";
import {TypeAllowedCustomLandingBaseNames} from "componentScreens/LandingPages";
import {IJobPosterPostView, IJobSeekerPostView} from "models/jobPostView";
import {IMembershipInfoResponse, IMidAds} from "../../screens/AccountMembership/_private/types/midView";
import {ensureOpenSooqBrand} from "./helpers/ensureOpenSooqBrand";
import {IMainCategories} from "models/brands";
import {iPostListingData} from "../../screens/PostView/_private/Api/types";

export default class SeoWidget {
  static _instances = {};

  private requestUri: string;
  private staticContentsAdapter: StaticContentsAdapter;
  private specsSeoAdapter: SpecsSeoAdapter;
  private singleNodeAdapter: SingleNodeAdapter;
  private seoItemAdapter: SeoItemAdapter;
  private localization: Localization;
  private isInitialized: boolean = false;

  constructor(
    private readonly all_countries: string,
    private readonly country: ICountryAsset = null,
    private readonly lang: string,
    private readonly essentialData: EssentialData,
    private readonly seoCache: ISeoCache,
    private readonly hostNoSubDomain: string,
    private readonly baseDomainWithCountry: string,
    private readonly fullHostName: string
  ) {
    this.country = country;
    this.lang = lang;
    this.baseDomainWithCountry = baseDomainWithCountry;
    this.essentialData = essentialData;
    this.hostNoSubDomain = hostNoSubDomain;
  }

  private async init() {
    if (this.isInitialized) {
      return;
    }
    await Localization.initOnce(this.all_countries);
    this.localization = new Localization(
      this.lang,
      this.country,
      this.hostNoSubDomain,
      this.fullHostName
    );
    this.staticContentsAdapter = new StaticContentsAdapter(
      this.essentialData,
      this.lang,
      this.country
    );
    this.seoItemAdapter = new SeoItemAdapter(
      this.seoCache,
      this.country,
      this.essentialData
    );
    this.singleNodeAdapter = new SingleNodeAdapter(
      this.essentialData,
      this.lang,
      this.country
    );
    this.specsSeoAdapter = new SpecsSeoAdapter(
      this.seoCache,
      this.country,
      this.essentialData
    );
    this.isInitialized = true;
  }

  static getInstance(
    all_countries: string,
    country: ICountryAsset,
    lang: string,
    essentialData: EssentialData,
    seoCache: ISeoCache,
    hostNoSubDomain: string,
    baseDomainWithCountry: string,
    fullHostName: string
  ): SeoWidget {
    if (!lang)
      throw new Error(
        "SeoWidget.getInstance require valid context with countryCode & lang"
      );

    if (!SeoWidget._instances?.[lang]?.[country?.abbreviation || "xx"]) {
      SeoWidget._instances[lang] = SeoWidget._instances[lang] || {};
      SeoWidget._instances[lang][country?.abbreviation || "xx"] =
        SeoWidget._instances[lang][country?.abbreviation || "xx"] || {};
      SeoWidget._instances[lang][country?.abbreviation || "xx"] = new SeoWidget(
        all_countries,
        country,
        lang,
        essentialData,
        seoCache,
        hostNoSubDomain,
        baseDomainWithCountry,
        fullHostName
      );
    }
    return SeoWidget._instances[lang][country?.abbreviation || "xx"];
  }

  public setRequestData(requestUri: string) {
    this.requestUri = requestUri;
    this.localization.setRequestUri(requestUri);
    this.staticContentsAdapter.setLocalization(this.localization);
    this.specsSeoAdapter.setLocalization(this.localization);
    this.singleNodeAdapter.setLocalization(this.localization);
    this.seoItemAdapter.setLocalization(this.localization);
  }

  public async getSeoData(
    screenKey: EnumScreenNames,
    payload: any = null,
    requestUri: string
  ): Promise<ISeoPage> {
    const {
      postsApiResponse,
      routerApiResponse,
      landingApiResponse,
      baseName,
    } = payload || {};

    await this.init(); // initialize should be async for dynamic Import countries on localization class
    this.setRequestData(requestUri);

    let data: ISeoPage;

    switch (screenKey) {
      case EnumScreenNames.WEATHER_SCREEN:
      case EnumScreenNames.PRAYER_TIME_SCREEN:
      case EnumScreenNames.LOAN_CALCULATOR_SCREEN:
      case EnumScreenNames.SITE_MAP_SCREEN:
      case EnumScreenNames.BUY_VS_RENT_SCREEN:
      case EnumScreenNames.OTHER_COUNTRIES_SCREEN:
      case EnumScreenNames.TERMS_OF_USE_SCREEN:
      case EnumScreenNames.PRIVACY_POLICY_SCREEN:
      case EnumScreenNames.ABOUT_US_SCREEN:
      case EnumScreenNames.OPENSOOQ_LOGO_SCREEN:
      case EnumScreenNames.ADVERTISING_PRODUCTS:
      case EnumScreenNames.SALES_REPRESENTATIVES:
      case EnumScreenNames.BANNER_ADVERTISING:
      case EnumScreenNames.OPEN_SHOP:
      case EnumScreenNames.SAFETY_TIPS_SCREEN:
      case EnumScreenNames.ADD_PICTURES_TIPS_SCREEN:
      case EnumScreenNames.SELLER_PAGES_SCREEN:
      case EnumScreenNames.SUPPORT_INDEX_SCREEN:
      case EnumScreenNames.ADVERTISING_PRODUCTS_PREMIUM:
      case EnumScreenNames.ADVERTISING_PRODUCTS_BUMPUP:
      case EnumScreenNames.ADVERTISING_PRODUCTS_TURBO:
      case EnumScreenNames.ADVERTISING_PRODUCTS_ELAS:
      case EnumScreenNames.ADVERTISING_PRODUCTS_VIP:
      case EnumScreenNames.ADVERTISING_PRODUCTS_WALLET:
      case EnumScreenNames.CONTACT_US_SCREEN:
      case EnumScreenNames.DOWNLOAD_APP_SCREEN:
      case EnumScreenNames.SELL_ANY_THING_SCREEN:
      case EnumScreenNames.MOTORS_SELL_IT_FOR_ME_SCREEN:
      case EnumScreenNames.MOTORS_SELL_MY_CAR_SCREEN:
      case EnumScreenNames.PROPERTY_SELL_MY_PROPERTY_SCREEN:
      case EnumScreenNames.PROPERTY_SELL_IT_FOR_ME_SCREEN:
      case EnumScreenNames.PROPERTY_RENT_MY_PROPERTY_SCREEN:
      case EnumScreenNames.PROPERTY_RENT_IT_FOR_ME_SCREEN:
      case EnumScreenNames.FIND_A_JOB_SCREEN:
      case EnumScreenNames.HIRE_PEOPLE_SCREEN:
        data = this.localization.replacePlaceHolders(
          this.staticContentsAdapter.getData(screenKey)
        );
        break;
      case EnumScreenNames.STATIC_PAGE_ADD_POST_SCREEN:
        data = this.localization.replacePlaceHolders(
            this.staticContentsAdapter.getData(screenKey)
        );

        data.link2 = this.seoItemAdapter.getSerpData(
            routerApiResponse as ILiteRouterApiResponse,
            null,
            postsApiResponse as IPostsApiResponse,
        )?.seoPage?.link2 || '';
        data.link2 = data.link2.replace("/en" , "/en/add-free-post").replace("/ar", "/ar/اضافة-اعلان-مجاني")
        break;

      case EnumScreenNames.POST_VIEW_SCREEN:
        const payloadData = payload as {
          postDetails: iPostListingData;
          category: ICategoryLiteAsset;
          subCategory: ICategoryLiteAsset;
        };
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getPostViewScreenSeoData(payloadData)
        );
        break;

      case EnumScreenNames.TOP_SOLD_CARS_SCREEN:
        const {allTopSoldCars} = payload;
        data = this.localization.replacePlaceHolders(
            this.singleNodeAdapter.getTopSoldCars(screenKey,allTopSoldCars)
        );
        break;

      case EnumScreenNames.JOB_VIEW_SCREEN:
        const payloadJobData = payload as {
          postDetails: IJobPosterPostView | IJobSeekerPostView;
        };
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getJobViewScreenSeoData(payloadJobData)
        );
        data = ensureOpenSooqBrand(data, this.lang);

        break;
      case EnumScreenNames.DLP_SCREEN:
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getDLPScreenSeoData(postsApiResponse, requestUri)
        );
        break;
      case EnumScreenNames.ARCHIVE_POST:
        data = this.singleNodeAdapter.getArchivePostSeoData(
          payload.postResponse
        );
        break;
      case EnumScreenNames.MID_SCREEN:
        const { userInfo, extendedQuery: extendedQueryMid } = payload;
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getMemberScreenSeoData(
            screenKey,
            userInfo as IMembershipInfoResponse,
            postsApiResponse as IPostsApiResponse,
            extendedQueryMid
          )
        );
        break;
      case EnumScreenNames.SHOP_LISTINGS_SCREEN:
        const { memberInfo, extendedQuery ,totalAdsCount} = payload;
        data = this.localization.replacePlaceHolders(
          this.seoItemAdapter.getShop({
            screenKey,
            memberInfo,
            extendedQuery,
            totalAdsCount,
          })
        );
        break;
      case EnumScreenNames.LOAN_CALCULATOR_DETAILS_SCREEN:
        const { bankDetails } = payload;
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getBanksDetailsScreenSeoData(
            screenKey,
            bankDetails as IBankDetails
          )
        );
        break;
      case EnumScreenNames.TAGS_DETAILS:
        const { postsByTagsApiResponse } = payload;
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getTagsDetailsScreenSeoData(
            screenKey,
            postsByTagsApiResponse as IPostsByTagsApiResponse,
            this.essentialData
          )
        );
        data = this.localization.replaceCountAndDatePlaceholders(
            data,
            postsByTagsApiResponse as IPostsByTagsApiResponse,
        );
        data = ensureOpenSooqBrand(data, this.lang);
        break;
      case EnumScreenNames.SUPPORT_FAQ_SCREEN:
        const { supportFaqUrlName, faqCategoriesAr, faqCategoriesEn } = payload;
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getSupportFaqSeoDetails(
            screenKey,
            supportFaqUrlName as string,
            faqCategoriesAr as IFaqCategory[],
            faqCategoriesEn as IFaqCategory[]
          )
        );
        break;
      case EnumScreenNames.TAGS_MAIN:
        const {
          tagsListData: { _meta },
        } = payload;
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getTagsMainScreenSeoData(
            screenKey,
            _meta as IMetaData
          )
        );
        data = ensureOpenSooqBrand(data, this.lang);
        break;
      case EnumScreenNames.TAGS_CATEGORIES:
        const { tagsListData, category } = payload;
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getTagsCategoriesScreenSeoData(
            screenKey,
            tagsListData._meta as IMetaData,
            category as ICategoryAsset
          )
        );
        data = ensureOpenSooqBrand(data, this.lang);
        break;
      case EnumScreenNames.WEATHER_SCREEN_WITH_CITY:
        let { city } = payload;
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getWeatherSeoDataByCity(
            screenKey,
            city as ICityAsset
          )
        );
        const text = JSON.stringify(data);
        data = this.localization.tr(text, "cityUrl", {
          cityUrl: urlHelper(city, this.lang),
        }) as ISeoPage;
        data = this.localization.tr(text, "cityName", {
          cityName: labelHelper(city, this.lang),
        }) as ISeoPage;
        data = JSON.parse(text) as ISeoPage;
        break;
      case EnumScreenNames.PRAYER_TIME_SCREEN_WITH_CITY:
        let { city: prayerCity } = payload;
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getPrayerSeoDataByCity(
            screenKey,
            prayerCity as ICityAsset
          )
        );
        const text2 = JSON.stringify(data);
        data = this.localization.tr(text2, "cityUrl", {
          cityUrl: urlHelper(prayerCity, this.lang),
        }) as ISeoPage;
        data = JSON.parse(text2) as ISeoPage;
        break;
      case EnumScreenNames.HOME_PAGE:
        const homeData = this.seoItemAdapter.getHome(
          routerApiResponse as ILiteRouterApiResponse
        );
        data = this.localization.replacePlaceWithLiteRouter(
          this.essentialData,
          homeData.seoItemModel,
          homeData.seoPage,
          routerApiResponse as ILiteRouterApiResponse
        );
        break;

      case EnumScreenNames.REAL_ESTATE_PRICES_SCREEN:
        const selectObject: ISelectedRealEstatePricesObjects =
          payload.selectedObjects;
        let level = EnumSeoItemLevel.LEVEL_REAL_ESTATE_PRICES_LANDING;
        if (selectObject.neighborhood || selectObject.city) {
          level = EnumSeoItemLevel.LEVEL_REAL_ESTATE_PRICES_OTHER;
        }
        const liteRouterResponse1: ILiteRouterApiResponse = {
          subCategoryId: Number(selectObject?.subCategory?.id) || null,
          neighborhoodId: Number(selectObject?.neighborhood?.id) || null,
          cityId: Number(selectObject?.city?.id) || null,
        };
        const realEstatePricesData = this.seoItemAdapter.getRealEstatePrices(
          EnumScreenNames.REAL_ESTATE_PRICES_SCREEN,
          liteRouterResponse1 as ILiteRouterApiResponse,
          level
        );

        data = this.localization.replacePlaceWithLiteRouter(
          this.essentialData,
          realEstatePricesData.seoItemModel,
          realEstatePricesData.seoPage,
          liteRouterResponse1
        );

        break;

      case EnumScreenNames.SHOPS_LIST_SCREEN:
        const shopListingWithCategory =
          this.seoItemAdapter.getShopListingWithCategory(
            payload.category as ICategoryAsset,
            payload.shopsApiResponse as IMidAds
          );

        data = this.localization.replacePlaceWithLiteRouter(
          this.essentialData,
          shopListingWithCategory.seoItemModel,
          shopListingWithCategory.seoPage,
          routerApiResponse as ILiteRouterApiResponse
        );
        break;

      case EnumScreenNames.SHOPS_SCREEN:
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getShopListingIndex(
            screenKey,
          )
        );

        break;

      case EnumScreenNames.CUSTOM_LANDING_PAGE:
        const customLandingPagePayload =
          this.seoItemAdapter.getCustomLandingPage(
            routerApiResponse as ILiteRouterApiResponse,
            baseName as TypeAllowedCustomLandingBaseNames
          );

        data = this.localization.replacePlaceWithLiteRouter(
          this.essentialData,
          customLandingPagePayload.seoItemModel,
          customLandingPagePayload.seoPage,
          routerApiResponse as ILiteRouterApiResponse
        );
        data = ensureOpenSooqBrand(data, this.lang);
        break;
      case EnumScreenNames.LANDING_PAGE:
        const landingPagePayload = this.seoItemAdapter.getLandingPage(
          routerApiResponse as ILiteRouterApiResponse,
          landingApiResponse as LandingApiResponse
        );

        data = this.localization.replacePlaceWithLiteRouter(
          this.essentialData,
          landingPagePayload.seoItemModel,
          landingPagePayload.seoPage,
          routerApiResponse as ILiteRouterApiResponse
        );
        data = ensureOpenSooqBrand(data, this.lang);
        break;
      case EnumScreenNames.SERP_SCREEN:
        let serpType = "normal";
        if (requestUri.includes("similarSerp") || requestUri.includes("recommendedSerp")) {
          serpType = "similar";
        }

        if (requestUri.includes("search=true")) {
          serpType = "search-no-seo";
        }

        const serpDataPayload = this.seoItemAdapter.getSerpData(
          routerApiResponse as ILiteRouterApiResponse,
          payload.page,
          postsApiResponse as IPostsApiResponse,
          serpType
        );

        if (requestUri.includes('page=')) {
          const staticSERP = this.staticContentsAdapter.getSerpData(EnumScreenNames.SERP_SCREEN);

          serpDataPayload.seoPage.pageMetaDescription = staticSERP.pageMetaDescription;
          serpDataPayload.seoPage.pageTitle = staticSERP.pageTitle;
        }

          data = this.localization.replacePlaceWithLiteRouter(
              this.essentialData,
              serpDataPayload.seoItemModel,
              serpDataPayload.seoPage,
              routerApiResponse as ILiteRouterApiResponse,
              payload.queryString as IFindPageQueryString
          );
        data.pageTitle = data.pageTitle.replaceAll(":    :", ":");
        data.pageMetaDescription = data.pageMetaDescription.replaceAll(":    :", ":");
        data = this.localization.replaceCountAndDatePlaceholders(
            data,
            postsApiResponse as IPostsApiResponse,
        );
        data = ensureOpenSooqBrand(data, this.lang);
        break;
      case EnumScreenNames.FIND_SCREEN:
        data = this.localization.replacePlaceHolders(
          this.singleNodeAdapter.getFindScreen(
            payload.queryString as IFindPageQueryString,
            screenKey
          )
        );
        data = ensureOpenSooqBrand(data, this.lang);
        break;
      case EnumScreenNames.CAR_PRICES_SCREEN:
        data = await this.specsSeoAdapter.getCarPrices(
          screenKey,
          payload.selectedObjects as ISelectedCarPricesSpecsObjects,
          payload.carSpecs as ICarPricesSpecsCarSpecs
        );

        data = this.localization.replacePlaceHolders(data);
        break;

      case EnumScreenNames.MOBILE_TABLET_PRICES_SPECS_SCREEN:
        data = await this.specsSeoAdapter.getMobileTabletData(
          screenKey,
          payload.selectedObjects as ISelectedTabletMobileSpecsObjects,
          payload.prices as IOtherModels,
          payload.otherModels as IOtherModels
        );
        data = this.localization.replacePlaceHolders(data);

        break;

      case EnumScreenNames.AUTOS_SERVICE_SCREEN:
        let seoItem = this.specsSeoAdapter.getAutoServiceData(screenKey, {
          selectedObjects:
            payload.selectedObjects as ISelectedAutoServiceCenterObjects,
          serviceCenterDetails: payload.serviceCenterDetails as IServiceCenter,
        });
        data = this.localization.replacePlaceHolders(seoItem);
        break;
      case EnumScreenNames.LANDING_BRANDS_SCREEN:
        data = this.localization.replacePlaceHolders(
            this.singleNodeAdapter.getLandingBrandCategories(
                screenKey,
                payload.categories as IMainCategories[]
            )
        );
        break;
        case EnumScreenNames.CATEGORY_BRANDS_SCREEN:
        data = this.localization.replacePlaceHolders(
            this.singleNodeAdapter.getLandingBrands(
                screenKey,
                payload.category,
                payload.brands,
            )
        );
        break;
        case EnumScreenNames.BRAND_SCREEN:
        data = this.localization.replacePlaceHolders(
            this.singleNodeAdapter.getCategoriesForBrand(
                screenKey,
                payload.category,
                payload.brands,
                payload.categoriesForBrand
            )
        );
        break;
      case EnumScreenNames.CAR_REPORTS_SCREEN:
        data = this.localization.replacePlaceHolders(
            this.staticContentsAdapter.getData(screenKey)
        );
        const reportPlaceholderValues = {
          jo: { en: "CarFax & Carseer", ar: "كارفاكس و كارسير" },
          sa: { en: "CarFax & Mojaz", ar: "كارفاكس و موجز" },
          default: { en: "CarFax", ar: "كارفاكس" },
        };
        const selectedCountry =
            reportPlaceholderValues[this.country.abbreviation] ||
            reportPlaceholderValues.default;
        data.pageTitle = data.pageTitle.replaceAll(
            "__reportPlaceholder__",
            selectedCountry[this.lang]
        );
        data.pageMetaDescription = data.pageMetaDescription.replaceAll(
            "__reportPlaceholder__",
            selectedCountry[this.lang]
        );
        break;
    }
    return data;
  }
}
